import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { useNavigate, generatePath } from 'react-router-dom';
import { Dispatch, RootState } from '../../store/store';
import ROUTE from '../../constants/route-constants';
import ControllerSearchSelect from '../../components/ControllerSearchSelect/ControllerSearchSelect';
import InteractiveButton from '../../components/InteractiveButton/InteractiveButton';
import { DEFAULT_INPUT_CLASS } from '../../components/InputControl/constants';

type ComponentProps = ReturnType<typeof mapState> & ReturnType<typeof mapDispatch>;

const CreateControllerSetup: React.FC<ComponentProps> = ({
  controllers,
  getControllers,
  createControllerSetupFromController,
}) => {
  const navigate = useNavigate();

  const [name, setName] = useState('');
  const [selectedControllerIds, setSelectedControllerIds] = useState<string[]>([]);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    getControllers();
  }, []);

  const canSubmit = name.trim().length > 0 && selectedControllerIds.length === 1 && !isSaving;

  const onSubmitHandler = async () => {
    if (!canSubmit) return;

    setIsSaving(true);
    const setup = await createControllerSetupFromController({
      controllerId: selectedControllerIds[0],
      name: name.trim(),
    });
    setIsSaving(false);

    if (setup) {
      navigate(generatePath(ROUTE.EDIT_CONTROLLER_SETUP, { id: setup.id }));
    }
  };

  return (
    <div className="p-create-controller-setup pb-4 md:pb-25">
      <div className="rounded-sm border border-stroke bg-white shadow-default dark:border-strokedark dark:bg-boxdark">
        <div className="border-b border-stroke px-7 py-4 dark:border-strokedark">
          <h3 className="font-medium text-black dark:text-white">Create Controller Setup</h3>
          <p className="mt-1 text-sm text-slate-gray">
            Pick a controller that's already configured the way you want - its current settings and channel values
            will be captured as the starting point for this setup, which you can then rename and fine-tune.
          </p>
        </div>
        <div className="flex flex-col gap-5 p-7">
          <div>
            <label className="mb-2.5 block font-medium text-black dark:text-white">Name</label>
            <input
              type="text"
              className={DEFAULT_INPUT_CLASS}
              placeholder="e.g. Standard 24 channel setup"
              value={name}
              onChange={(event) => setName(event.target.value)}
            />
          </div>
          <div>
            <label className="mb-2.5 block font-medium text-black dark:text-white">Source controller</label>
            <ControllerSearchSelect
              controllers={controllers}
              selectedIds={selectedControllerIds}
              onChange={setSelectedControllerIds}
            />
          </div>
          <div className="flex justify-end">
            <InteractiveButton
              text={isSaving ? 'Creating...' : 'Create'}
              onClick={onSubmitHandler}
              disabled={!canSubmit}
              isLoading={isSaving}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

const mapState = (state: RootState) => ({
  controllers: state.controllers.controllers_list,
});

const mapDispatch = (dispatch: Dispatch) => ({
  getControllers: dispatch.controllers.getControllers,
  createControllerSetupFromController: dispatch.controller_setups.createControllerSetupFromController,
});

export default connect(mapState, mapDispatch)(CreateControllerSetup);
